import { MoveStrategy } from './MoveStrategy';
import { Position, BoardState } from '../model/BoardState';
import { Piece } from '../model/Piece';
import { CaptureMoveDetector } from './CaptureMoveDetector';
import logger from '../utils/Logger';

export class ComputerCaptureQueenStrategy implements MoveStrategy {
  // Method to get legal moves for the computer to capture the player's queen
  getLegalMoves(_position: Position | null, boardState: BoardState): Position[] {
    // Filter out the computer's pieces (white pieces)
    const computerPieces = boardState.pieces.filter(piece => piece.color === 'white');
    // Find the player's queen (black queen)
    const playerQueen = boardState.pieces.find(piece => piece.color === 'black' && piece.type === 'queen');

    // If there is no player's queen on the board, return an empty array
    if (!playerQueen) {
      return [];
    }

    let closestPiece: Piece | undefined;
    let closestDistance = Infinity;
    let legalMoves: Position[] = [];

    // Iterate over each computer piece to find the closest one to the player's queen
    for (const piece of computerPieces) {
      const distance = Math.sqrt(Math.pow(piece.position.row - playerQueen.position.row, 2) + Math.pow(piece.position.col - playerQueen.position.col, 2));
      const moves = boardState.getLegalMoves(piece).filter(this.isWithinBounds);

      // Update the closest piece and its legal moves if the current piece is closer
      if (distance < closestDistance && moves.length > 0) {
        closestPiece = piece;
        closestDistance = distance;
        legalMoves = moves;
      }
    }

    // If no closest piece is found or it has no legal moves, return an empty array
    if (!closestPiece || legalMoves.length === 0) {
      return [];
    }

    logger.debug(`Closest piece to player queen is ${closestPiece.type} at (${closestPiece.position.row}, ${closestPiece.position.col})`);

    // Separate the legal moves into queen capture moves, other capture moves, and non-capture moves
    const queenCaptureMoves = legalMoves.filter(move => this.isQueenCaptureMove(move, boardState));
    const otherCaptureMoves = legalMoves.filter(move => 
      CaptureMoveDetector.isCaptureMove(move, boardState, 'white') && 
      !this.isQueenCaptureMove(move, boardState)
    );
    const nonCaptureMoves = legalMoves.filter(move => !CaptureMoveDetector.isCaptureMove(move, boardState, 'white'));

    // Sort the other capture moves by piece value
    const prioritizedCaptureMoves = CaptureMoveDetector.prioritizeCaptureMoves(otherCaptureMoves, boardState);

    // Prioritize queen capture moves over other capture moves and non-capture moves
    const prioritizedMoves = [...queenCaptureMoves, ...prioritizedCaptureMoves, ...nonCaptureMoves];

    // Log and assign the piece to each move
    prioritizedMoves.forEach(move => {
      logger.debug(`Piece ${closestPiece!.type} can move to (${move.row}, ${move.col})`);
      move.piece = closestPiece;
    });

    // Return the prioritized moves
    return prioritizedMoves;
  }

  getStrategyName(): string {
    return "Computer Capture Queen Strategy";
  }

  // Helper method to check if a position is within the bounds of the board
  private isWithinBounds(position: Position): boolean {
    return position.row >= 0 && position.row < 8 && position.col >= 0 && position.col < 8;
  }

  // Helper method to check if a move captures any of the player's pieces
  private isCaptureMove(move: Position, boardState: BoardState): boolean {
    return CaptureMoveDetector.isCaptureMove(move, boardState, 'white');
  }

  // Helper method to check if a move captures the player's queen
  private isQueenCaptureMove(move: Position, boardState: BoardState): boolean {
    return boardState.pieces.some(p => p.position.row === move.row && p.position.col === move.col && p.color === 'black' && p.type === 'queen');
  }
}

